import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, FileText, Upload, Wand2, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const CopedantPDFImportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1); // 1: upload, 2: edit text, 3: preview
  const [loading, setLoading] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [copedantName, setCopedantName] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [parseError, setParseError] = useState(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }
    
    setLoading(true);
    setParseError(null);
    
    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result.split(',')[1];
        
        try {
          const response = await api.post(
            '/copedants/import-pdf/extract',
            { file_data: base64Data, file_name: file.name }
          );
          
          if (response.data.text) {
            setExtractedText(response.data.text);
            setCopedantName(file.name.replace('.pdf', ''));
            setStep(2);
            toast.success('Text extracted successfully');
          } else {
            setParseError('No text could be extracted from this PDF. If your PDF is scanned, please use CSV import instead.');
          }
        } catch (error) {
          console.error('Extract error:', error);
          const message = error.response?.data?.detail || 'Failed to extract text from PDF';
          setParseError(message);
          toast.error(message);
        } finally {
          setLoading(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File read error:', error);
      toast.error('Failed to read file');
      setLoading(false);
    }
  };

  const handleParseText = async () => {
    if (!extractedText.trim()) {
      toast.error('Please enter or modify the extracted text');
      return;
    }
    
    setLoading(true);
    setParseError(null);
    
    try {
      const response = await api.post(
        '/copedants/import-pdf/parse',
        { 
          text: extractedText, 
          name: copedantName || 'Imported Copedant'
        }
      );
      
      setParsedData(response.data);
      setStep(3);
      toast.success('Copedant parsed successfully');
    } catch (error) {
      console.error('Parse error:', error);
      const message = error.response?.data?.detail || 'Failed to parse copedant from text';
      setParseError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCopedant = async () => {
    if (!parsedData) return;
    
    setLoading(true);
    
    try {
      const response = await api.post(
        '/copedants',
        {
          name: copedantName || 'Imported Copedant',
          description: 'Imported from PDF',
          strings: parsedData.strings,
          controls: parsedData.controls
        }
      );
      
      toast.success('Copedant created successfully');
      navigate(`/copedants/${response.data.copedant_id}/edit`);
    } catch (error) {
      console.error('Create error:', error);
      const message = error.response?.data?.detail || 'Failed to create copedant';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const isPro = user?.is_pro;

  return (
    <div className="min-h-screen bg-ss-bg">
      <div className="noise-overlay"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
            className="text-ss-muted hover:text-ss-text"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text">
              Import PDF
            </h1>
            <p className="text-xs text-ss-muted">Extract copedant data from PDF documents</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s ? 'bg-ss-cyan text-black' : 'bg-ss-surface text-ss-muted border border-ss-border'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-0.5 mx-2 ${step > s ? 'bg-ss-cyan' : 'bg-ss-border'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Upload PDF */}
        {step === 1 && (
          <Card className="bg-ss-surface border-ss-border">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-ss-text flex items-center gap-2">
                <FileText className="w-6 h-6 text-ss-cyan" />
                Upload PDF
              </CardTitle>
              <CardDescription className="text-ss-muted">
                Upload a PDF containing copedant data. The text will be extracted automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-ss-border hover:border-ss-cyan/50 rounded-xl p-12 text-center cursor-pointer transition-colors"
              >
                <Upload className="w-12 h-12 text-ss-muted mx-auto mb-4" />
                <p className="text-ss-text font-semibold mb-2">Click to upload PDF</p>
                <p className="text-sm text-ss-muted">or drag and drop</p>
                <p className="text-xs text-ss-muted mt-4">Maximum file size: 10MB</p>
              </div>

              {parseError && (
                <div className="p-4 bg-ss-red/10 border border-ss-red/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-ss-red flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-ss-red font-semibold">Extraction Failed</p>
                      <p className="text-sm text-ss-muted mt-1">{parseError}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-4 bg-ss-surface-highlight rounded-lg">
                <p className="text-sm text-ss-muted">
                  <strong className="text-ss-text">Note:</strong> PDF import works best with text-based PDFs. 
                  If your PDF is a scanned image, please use{' '}
                  <button 
                    onClick={() => navigate('/copedants/import')}
                    className="text-ss-cyan hover:underline"
                  >
                    CSV import
                  </button>{' '}
                  instead.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Edit Extracted Text */}
        {step === 2 && (
          <Card className="bg-ss-surface border-ss-border">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-ss-text flex items-center gap-2">
                <Wand2 className="w-6 h-6 text-ss-cyan" />
                Edit Extracted Text
              </CardTitle>
              <CardDescription className="text-ss-muted">
                Review and modify the extracted text before parsing.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-ss-muted">Copedant Name</Label>
                <Input
                  value={copedantName}
                  onChange={(e) => setCopedantName(e.target.value)}
                  placeholder="My Copedant"
                  className="mt-1 bg-ss-bg border-ss-border text-ss-text"
                  data-testid="copedant-name-input"
                />
              </div>

              <div>
                <Label className="text-ss-muted">Extracted Text</Label>
                <Textarea
                  value={extractedText}
                  onChange={(e) => setExtractedText(e.target.value)}
                  placeholder="Extracted copedant data..."
                  rows={15}
                  className="mt-1 bg-ss-bg border-ss-border text-ss-text font-mono text-sm"
                  data-testid="extracted-text-input"
                />
                <p className="text-xs text-ss-muted mt-2">
                  The parser looks for: string numbers (1-14), note names (C, D, E, F, G, A, B with optional #/b), 
                  control names (A, B, C, LKL, RKR, etc.), and semitone changes (+1, -2, etc.)
                </p>
              </div>

              {parseError && (
                <div className="p-4 bg-ss-red/10 border border-ss-red/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-ss-red flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-ss-red font-semibold">Parse Error</p>
                      <p className="text-sm text-ss-muted mt-1">{parseError}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="border-ss-border text-ss-text"
                >
                  Back
                </Button>
                <Button
                  onClick={handleParseText}
                  disabled={loading || !extractedText.trim()}
                  data-testid="parse-text-btn"
                  className="flex-1 bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                      Parsing...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Parse & Preview
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Preview and Create */}
        {step === 3 && parsedData && (
          <Card className="bg-ss-surface border-ss-border">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-ss-text flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-500" />
                Preview Copedant
              </CardTitle>
              <CardDescription className="text-ss-muted">
                Review the parsed data before creating the copedant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-ss-muted">Copedant Name</Label>
                <Input
                  value={copedantName}
                  onChange={(e) => setCopedantName(e.target.value)}
                  className="mt-1 bg-ss-bg border-ss-border text-ss-text"
                />
              </div>

              {/* Strings Preview */}
              <div>
                <Label className="text-ss-muted mb-2 block">
                  Strings ({parsedData.strings.length})
                </Label>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  {parsedData.strings.map((s, i) => (
                    <div 
                      key={i}
                      className="p-2 bg-ss-bg border border-ss-border rounded text-center"
                    >
                      <span className="text-xs text-ss-muted block">{s.string_number}</span>
                      <span className="text-sm font-mono text-ss-cyan">{s.open_note}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Controls Preview */}
              <div>
                <Label className="text-ss-muted mb-2 block">
                  Controls ({parsedData.controls.length})
                </Label>
                <div className="space-y-2">
                  {parsedData.controls.map((ctrl, i) => (
                    <div 
                      key={i}
                      className="p-3 bg-ss-bg border border-ss-border rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-ss-text">{ctrl.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          ctrl.control_type === 'lever' 
                            ? 'bg-ss-purple/20 text-ss-purple' 
                            : 'bg-ss-cyan/20 text-ss-cyan'
                        }`}>
                          {ctrl.control_type}
                        </span>
                      </div>
                      {ctrl.changes.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {ctrl.changes.map((ch, j) => (
                            <span key={j} className="text-xs px-2 py-1 bg-ss-surface-highlight rounded text-ss-muted">
                              String {ch.string_number}: {ch.semitone_change > 0 ? '+' : ''}{ch.semitone_change}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="border-ss-border text-ss-text"
                >
                  Back
                </Button>
                <Button
                  onClick={handleCreateCopedant}
                  disabled={loading}
                  data-testid="create-copedant-btn"
                  className="flex-1 bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Create Copedant
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CopedantPDFImportPage;
